import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const CAPSTONE = '#E85D75';
const TEXT_MUTED = '#5a5f7d';

interface PersonNode {
  id: number;
  label: string;
  likes: number;
  x: number;
  y: number;
  targetY: number;
}

export function RankLadder() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 280;
    const cx = width / 2;
    const nodeR = 10;

    // Generate person nodes with random initial positions
    const likeCounts = [342, 215, 189, 147, 98, 64, 31, 12];
    const labels = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

    // Shuffled initial positions
    const shuffledIndices = d3.shuffle(d3.range(likeCounts.length));

    const nodes: PersonNode[] = likeCounts.map((likes, i) => {
      const shuffled = shuffledIndices[i];
      const col = shuffled % 4;
      const row = Math.floor(shuffled / 4);
      return {
        id: i,
        label: labels[i],
        likes,
        x: 50 + col * 55 + (Math.random() - 0.5) * 20,
        y: 40 + row * 70 + (Math.random() - 0.5) * 20,
        targetY: 0,
      };
    });

    // Compute sorted target Y positions (sorted by likes, highest at top)
    const sorted = [...nodes].sort((a, b) => b.likes - a.likes);
    const ladderStartY = 22;
    const ladderGap = 24;
    sorted.forEach((node, i) => {
      node.targetY = ladderStartY + i * ladderGap;
    });

    // Draw person icons (circle with small head)
    const personGroup = svg.append('g');

    const people = personGroup
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('transform', (d) => `translate(${d.x}, ${d.y})`);

    // Body circle
    people
      .append('circle')
      .attr('r', nodeR)
      .attr('fill', CAPSTONE)
      .attr('opacity', 0.7);

    // Head
    people
      .append('circle')
      .attr('cy', -nodeR - 3)
      .attr('r', 4)
      .attr('fill', CAPSTONE)
      .attr('opacity', 0.7);

    // Label
    people
      .append('text')
      .attr('y', 3)
      .attr('text-anchor', 'middle')
      .attr('font-size', 8)
      .attr('font-weight', 700)
      .attr('fill', '#fff')
      .text((d) => d.label);

    // Like count (initially hidden)
    const likesText = people
      .append('text')
      .attr('x', nodeR + 8)
      .attr('y', 3)
      .attr('text-anchor', 'start')
      .attr('font-size', 8)
      .attr('fill', TEXT_MUTED)
      .attr('opacity', 0)
      .text((d) => `${d.likes}`);

    // Phase 1: Show scattered nodes for a moment, then sort
    const sortTimer = d3.timeout(() => {
      // Move all nodes to ladder formation
      people
        .transition()
        .duration(600)
        .delay((_, i) => i * 60)
        .ease(d3.easeCubicInOut)
        .attr('transform', (d) => `translate(${cx}, ${d.targetY})`);

      // Show like counts
      likesText
        .transition()
        .duration(300)
        .delay((_, i) => 400 + i * 60)
        .attr('opacity', 1);
    }, 1200);

    // Phase 2: Draw ladder rungs / ranking lines
    const ladderTimer = d3.timeout(() => {
      const ladderGroup = svg.append('g').attr('class', 'ladder');

      sorted.forEach((node, i) => {
        // Horizontal rung
        ladderGroup
          .append('line')
          .attr('x1', cx - 40)
          .attr('y1', node.targetY)
          .attr('x2', cx - 40)
          .attr('y2', node.targetY)
          .attr('stroke', TEXT_MUTED)
          .attr('stroke-width', 0.8)
          .attr('opacity', 0.3)
          .transition()
          .duration(300)
          .delay(i * 50)
          .attr('x2', cx + 60);

        // Rank number
        ladderGroup
          .append('text')
          .attr('x', cx - 50)
          .attr('y', node.targetY + 3)
          .attr('text-anchor', 'end')
          .attr('font-size', 8)
          .attr('font-weight', 600)
          .attr('fill', TEXT_MUTED)
          .attr('opacity', 0)
          .text(`#${i + 1}`)
          .transition()
          .duration(200)
          .delay(100 + i * 50)
          .attr('opacity', 0.6);
      });

      // Vertical ladder line
      ladderGroup
        .append('line')
        .attr('x1', cx - 40)
        .attr('y1', ladderStartY)
        .attr('x2', cx - 40)
        .attr('y2', ladderStartY)
        .attr('stroke', TEXT_MUTED)
        .attr('stroke-width', 1)
        .attr('opacity', 0.4)
        .transition()
        .duration(400)
        .attr('y2', ladderStartY + (sorted.length - 1) * ladderGap);
    }, 2200);

    // Phase 3: Highlight top node, dim bottom ones
    const highlightTimer = d3.timeout(() => {
      people
        .select('circle:first-child')
        .transition()
        .duration(400)
        .attr('opacity', (d) => {
          const rank = sorted.findIndex((s) => s.id === d.id);
          return 0.9 - rank * 0.08;
        });

      people
        .select('circle:nth-child(2)')
        .transition()
        .duration(400)
        .attr('opacity', (d) => {
          const rank = sorted.findIndex((s) => s.id === d.id);
          return 0.9 - rank * 0.08;
        });
    }, 3000);

    return () => {
      sortTimer.stop();
      ladderTimer.stop();
      highlightTimer.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-arrow-up-wide-short" /> Social Feed &mdash; Status Compression
      </h4>
      <svg ref={svgRef} viewBox="0 0 280 220" />
      <p className="viz-container__caption">
        Belonging requires comparison.
      </p>
    </div>
  );
}
